import { useCallback } from 'react'

import { truncate } from 'lodash'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { type TermsMethodsKindEnum } from '@webapp/modules/purchasing/entities/enums/TermsMethodsKindEnum'
import { useMutateCreateTermOrMethod } from '@webapp/modules/purchasing/features/create-term-or-method/models'
import { type CreateTermOrMethodFormSchema } from '@webapp/modules/purchasing/features/create-term-or-method/views/CreateTermOrMethodForm'
import messages from './messages'

// Angular `_truncate(name, { length: 12 })` toast parity
const TOAST_NAME_TRUNCATE_LENGTH = 12

export interface UseCreateTermOrMethodFormPresenterParams {
  kind: TermsMethodsKindEnum
  onSuccess: (id: number) => void
}

export const useCreateTermOrMethodFormPresenter = ({
  kind,
  onSuccess,
}: UseCreateTermOrMethodFormPresenterParams) => {
  const intl = useProcurifyIntl()
  const { mutateAsync, isLoading } = useMutateCreateTermOrMethod(kind)

  const handleSubmit = useCallback(
    async (values: CreateTermOrMethodFormSchema) => {
      try {
        const { id } = await mutateAsync(values)

        toast.success({
          title: intl.formatMessage(messages.successTitle),
          children: intl.formatMessage(messages.successMessage, {
            name: truncate(values.name, {
              length: TOAST_NAME_TRUNCATE_LENGTH,
            }),
          }),
        })

        // The Feature Set closes the modal and sets the CREATED indicator
        // (BHV-029)
        onSuccess(id)
      } catch (error) {
        const errorMessage =
          getMessageFromError(error) ||
          intl.formatMessage(messages.defaultErrorMessage)

        toast.error({
          title: intl.formatMessage(messages.errorTitle),
          children: errorMessage,
        })
      }
    },
    [intl, mutateAsync, onSuccess]
  )

  return {
    states: {
      isSaving: isLoading,
    },
    handlers: {
      handleSubmit,
    },
  }
}
